import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { loadError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euro, fmtDate, nights, STATUS, PAYMENT_METHODS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import BookingDialog from "@/components/BookingDialog";
import { Plus, Pencil, Trash2, Wallet, Search, Mail, Clock, History, Copy, BellRing, BellOff, Bell, Contact, RotateCw, Users } from "lucide-react";
import { BookingHistory, HistoryPdfButton } from "@/components/BookingHistory";
import { RegularBadge } from "@/components/GuestBadges";
import { GroupActions } from "@/components/GroupActions";
import { useNavigate, useSearchParams } from "react-router-dom";

const guestKey = (b) => {
  const email = (b.guest_email || "").trim().toLowerCase();
  return email ? `e:${email}` : `n:${(b.guest_name || "").trim().toLowerCase()}|${b.birth_date || ""}`;
};

const ReminderStatus = ({ b }) => {
  if (b.reminder_sent) return <span data-testid={`reminder-status-${b.id}`} data-reminder="sent" title="Promemoria check-in inviato" className="inline-flex text-emerald-600"><BellRing className="w-3.5 h-3.5" /></span>;
  if (b.reminder_attempts > 0) {
    const next = b.reminder_next_retry ? new Date(b.reminder_next_retry).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—";
    const title = `Promemoria non inviato: ${b.reminder_attempts} tentativ${b.reminder_attempts === 1 ? "o" : "i"}${b.reminder_attempts >= 5 ? " (limite raggiunto)" : `, prossimo tentativo ${next}`}${b.reminder_last_error ? `\nUltimo errore: ${b.reminder_last_error}` : ""}`;
    return <span data-testid={`reminder-status-${b.id}`} data-reminder="failed" title={title} className="inline-flex items-center gap-0.5 text-orange-600"><BellOff className="w-3.5 h-3.5" /><span className="text-[10px] font-semibold">{b.reminder_attempts}</span></span>;
  }
  return <span data-testid={`reminder-status-${b.id}`} data-reminder="pending" title={b.guest_email ? "Promemoria non ancora dovuto (inviato il giorno prima dell'arrivo)" : "Nessuna email ospite: promemoria non inviabile"} className="inline-flex text-muted-foreground/50"><Bell className="w-3.5 h-3.5" /></span>;
};
import { toast } from "sonner";

export default function Bookings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canDelete = isAdmin || !!user?.can_delete_bookings;
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dupFrom, setDupFrom] = useState(null);
  const [histBooking, setHistBooking] = useState(null);
  const [filter, setFilter] = useState("all");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");

  // payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payBooking, setPayBooking] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", method: "Contanti", notes: "" });
  const [payments, setPayments] = useState([]);

  const load = () => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(loadError);
    api.get("/rooms").then((r) => setRooms(r.data)).catch(loadError);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (filter !== "all" && b.status !== filter) return false;
      if (q && !`${b.guest_name} ${b.room_name}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [bookings, filter, q]);

  const openNew = () => { setEditing(null); setDupFrom(null); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setDupFrom(null); setOpen(true); };
  const openDuplicate = (b) => { setEditing(null); setDupFrom(b); setOpen(true); };

  const setStatus = async (b, status) => {
    await api.patch(`/bookings/${b.id}/status`, { status });
    load();
  };

  const [retrying, setRetrying] = useState(null);
  const retryReminder = async (b) => {
    setRetrying(b.id);
    try {
      await api.post(`/bookings/${b.id}/send-reminder`);
      toast.success(`Promemoria inviato a ${b.guest_email}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
    finally { setRetrying(null); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Eliminare la prenotazione di ${b.guest_name}?`)) return;
    await api.delete(`/bookings/${b.id}`);
    toast.success("Prenotazione eliminata");
    load();
  };

  const sendConfirmation = async (b) => {
    if (!b.guest_email) { toast.error("Nessuna email registrata per questo ospite"); return; }
    try {
      await api.post(`/bookings/${b.id}/send-confirmation`);
      toast.success(`Conferma inviata a ${b.guest_email}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openPay = async (b) => {
    setPayBooking(b);
    setPayForm({ amount: String(b.balance > 0 ? b.balance : ""), method: "Contanti", notes: "" });
    const r = await api.get(`/bookings/${b.id}/payments`);
    setPayments(r.data);
    setPayOpen(true);
  };

  const addPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error("Inserisci un importo valido"); return; }
    try {
      await api.post("/payments", { booking_id: payBooking.id, amount: Number(payForm.amount), method: payForm.method, notes: payForm.notes });
      toast.success("Pagamento registrato");
      const r = await api.get(`/bookings/${payBooking.id}/payments`);
      setPayments(r.data);
      setPayForm({ amount: "", method: "Contanti", notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delPayment = async (id) => {
    await api.delete(`/payments/${id}`);
    const r = await api.get(`/bookings/${payBooking.id}/payments`);
    setPayments(r.data);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Ospiti</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Prenotazioni</h1>
        </div>
        <Button data-testid="new-booking-button" onClick={openNew} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-11 px-5 font-heading">
          <Plus className="w-4 h-4 mr-2" /> Nuova Prenotazione
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="booking-search" placeholder="Cerca ospite o camera…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="booking-filter" className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="confirmed">Confermata</SelectItem>
            <SelectItem value="pending">In attesa</SelectItem>
            <SelectItem value="checkedin">Check-in</SelectItem>
            <SelectItem value="checkedout">Check-out</SelectItem>
            <SelectItem value="cancelled">Annullata</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="bookings-table">
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Ospite</TableHead>
                <TableHead>Camera</TableHead>
                <TableHead>Soggiorno</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Totale</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((b) => {
                const s = STATUS[b.status] || STATUS.confirmed;
                return (
                  <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className="hover:bg-secondary/30">
                    <TableCell>
                      <div className="font-medium flex items-center gap-2 flex-wrap">{b.guest_name}<RegularBadge stays={b.guest_stays} testId={`regular-badge-${b.id}`} className="text-[10px] py-0" />
                        {b.group_size > 1 && <Badge data-testid={`group-badge-${b.id}`} className="bg-sky-100 text-sky-800 border-0 text-[10px] py-0 gap-1"><Users className="w-3 h-3" />Gruppo · {b.group_size} camere</Badge>}</div>
                      <div className="text-xs text-muted-foreground">{b.nationality} · {b.num_guests} ospiti</div>
                    </TableCell>
                    <TableCell className="text-sm">{b.room_name}</TableCell>
                    <TableCell className="text-sm">
                      {fmtDate(b.check_in)} → {fmtDate(b.check_out)}
                      <div className="text-xs text-muted-foreground">
                        {nights(b.check_in, b.check_out)} notti
                        <span className="ml-2 inline-flex align-middle items-center gap-1"><ReminderStatus b={b} />
                          {!b.reminder_sent && b.reminder_attempts > 0 && b.guest_email && (
                            <button data-testid={`retry-reminder-${b.id}`} onClick={() => retryReminder(b)} disabled={retrying === b.id} title="Riprova invio promemoria" className="text-orange-600 hover:text-orange-800 disabled:opacity-50">
                              <RotateCw className={`w-3.5 h-3.5 ${retrying === b.id ? "animate-spin" : ""}`} />
                            </button>
                          )}
                        </span>
                        {b.arrival_time && <span data-testid={`arrival-time-${b.id}`} className="ml-2 inline-flex items-center gap-1 text-accent font-medium" title={b.arrival_notes || ""}><Clock className="w-3 h-3" />arrivo {b.arrival_time}</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => setStatus(b, v)}>
                        <SelectTrigger data-testid={`status-select-${b.id}`} className="h-8 w-[130px] border-0 p-0 focus:ring-0">
                          <Badge variant="secondary" className={`${s.bg} ${s.text} border-0`}>{s.label}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">Confermata</SelectItem>
                          <SelectItem value="pending">In attesa</SelectItem>
                          <SelectItem value="checkedin">Check-in</SelectItem>
                          <SelectItem value="checkedout">Check-out</SelectItem>
                          <SelectItem value="cancelled">Annullata</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right font-medium">{euro(b.total_amount)}</TableCell>
                    <TableCell className="text-right">
                      <span className={b.balance > 0 ? "text-orange-600 font-medium" : "text-emerald-600 font-medium"}>
                        {b.balance > 0 ? euro(b.balance) : "Saldato"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" data-testid={`send-mail-${b.id}`} onClick={() => sendConfirmation(b)} title="Invia conferma email">
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`pay-button-${b.id}`} onClick={() => openPay(b)} title="Pagamenti">
                        <Wallet className="w-4 h-4" />
                      </Button>
                      <GroupActions booking={b} onDone={load} />
                      <Button variant="ghost" size="sm" data-testid={`guest-profile-${b.id}`} onClick={() => navigate(`/ospiti?sel=${encodeURIComponent(guestKey(b))}`)} title="Scheda ospite">
                        <Contact className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`history-button-${b.id}`} onClick={() => setHistBooking(b)} title="Cronologia spostamenti" className={b.history?.length ? "text-accent" : "text-muted-foreground"}>
                        <History className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`duplicate-booking-${b.id}`} onClick={() => openDuplicate(b)} title="Ripeti prenotazione per lo stesso ospite">
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`edit-booking-${b.id}`} onClick={() => openEdit(b)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="sm" data-testid={`delete-booking-${b.id}`} onClick={() => remove(b)} className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Nessuna prenotazione trovata.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <BookingDialog open={open} onOpenChange={setOpen} rooms={rooms} booking={editing} duplicateFrom={dupFrom} onSaved={load} />

      {/* Payment dialog */}
      <Dialog open={!!histBooking} onOpenChange={(v) => !v && setHistBooking(null)}>
        <DialogContent className="max-w-md" data-testid="history-dialog">
          {histBooking && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl">{histBooking.guest_name}</DialogTitle></DialogHeader>
              <div className="text-sm space-y-1">
                <div className="flex justify-between border-b border-border/60 py-1.5"><span className="text-muted-foreground">Camera</span><span className="font-medium">{histBooking.room_name}</span></div>
                <div className="flex justify-between border-b border-border/60 py-1.5"><span className="text-muted-foreground">Soggiorno</span><span className="font-medium">{fmtDate(histBooking.check_in)} → {fmtDate(histBooking.check_out)}</span></div>
              </div>
              <BookingHistory history={histBooking.history} rooms={rooms} />
              <div className="mt-3"><HistoryPdfButton booking={histBooking} /></div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Pagamenti — {payBooking?.guest_name}</DialogTitle>
          </DialogHeader>
          {payBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center bg-secondary/50 rounded-lg p-3">
                <div><p className="text-xs text-muted-foreground">Totale</p><p className="font-heading font-semibold">{euro(payBooking.total_amount)}</p></div>
                <div><p className="text-xs text-muted-foreground">Versato</p><p className="font-heading font-semibold text-emerald-600">{euro(payBooking.paid_amount)}</p></div>
                <div><p className="text-xs text-muted-foreground">Saldo</p><p className="font-heading font-semibold text-orange-600">{euro(payBooking.balance)}</p></div>
              </div>

              <div className="space-y-2 max-h-40 overflow-y-auto">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border border-border rounded-lg px-3 py-2">
                    <div><span className="font-medium">{euro(p.amount)}</span> <span className="text-muted-foreground">· {p.method}</span></div>
                    <button onClick={() => delPayment(p.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {payments.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">Nessun pagamento registrato.</p>}
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-border pt-4">
                <div><Label className="text-xs uppercase tracking-wider">Importo €</Label><Input data-testid="payment-amount" type="number" className="mt-1.5" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Metodo</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger data-testid="payment-method-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Chiudi</Button>
            <Button data-testid="add-payment-button" onClick={addPayment} className="bg-accent hover:bg-accent/90 text-accent-foreground">Registra pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
